'use client';

import { TImageViewportWidths, Image } from '@/components/atoms/Image/Image';
import {
  ICommonProps,
  IScrollEffect,
  TImageAsset,
  TVideoAsset,
  TVideoType,
} from '@/types/global';
import { Video } from '@/components/atoms/Video/Video';
import { ComponentContainer } from '@/components/atoms/ComponentContainer/ComponentContainer';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useRef } from 'react';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import { TwoColContainer } from '@/components/atoms/TwoColContainer/TwoColContainer';
import * as Styled from './MediasSection.styled';
import { handleClickToggleGallerySliderModal } from '@/utils/generic';

gsap.registerPlugin(ScrollTrigger);

export interface IMediasSection extends ICommonProps {
  gridLayout: TGridLayouts;
  includeInSlider?: boolean;
  assets: (TImageAsset | TVideoAsset)[];
  scrollEffect?: IScrollEffect;
  titleVideo?: string;
}

export type TGridLayouts =
  | 'C_1-col--1-img-2:1'
  | 'D_1-col--1-img-16:9'
  | 'E_1-img-2:1-left'
  | 'F_1-img-2:1-right'
  | 'G_1-img-16:9-left'
  | 'H_1-img-16:9-right'
  | 'K_2-col--2-imgs-2:1'
  | 'L_2-col--2-imgs-16:9'
  | 'M_2-col--2-imgs-1:1'
  | 'N_2-col--3-imgs'
  | 'O_3-col--3-imgs'
  | 'P_2-col--4-imgs-right'
  | 'Q_2-col--4-imgs'
  | 'R_3-col--5-imgs';
// | '2-col--3-1-imgs'
// | '1-2-col--3-imgs'
// | '2-col--2-imgs-16:9'
// | '1-3-col--4-imgs'
// | '2-col--2-imgs-square'

export function MediasSection({
  gridLayout = 'N_2-col--3-imgs',
  assets = [],
  width = 'full',
  marginBottom = 'default',
  marginBottomOnMobile = 'none',
  includeInSlider = true,
  isProject = true,
  orderInPage,
  animationContainer = {
    type: 'none',
  },
  scrollAnimation = {
    type: 'none',
  },
  titleVideo
}: IMediasSection) {
  // TODO: ver si se puede limitar en el CMS que según el tipo de layout se limiten las imágenes
  const imagesLimit: Record<TGridLayouts, number> = {
    'C_1-col--1-img-2:1': 1,
    'D_1-col--1-img-16:9': 1,
    'E_1-img-2:1-left': 1,
    'F_1-img-2:1-right': 1,
    'G_1-img-16:9-left': 1,
    'H_1-img-16:9-right': 1,
    'K_2-col--2-imgs-2:1': 2,
    'L_2-col--2-imgs-16:9': 2,
    'M_2-col--2-imgs-1:1': 2,
    'N_2-col--3-imgs': 3,
    'O_3-col--3-imgs': 3,
    'P_2-col--4-imgs-right': 4,
    'Q_2-col--4-imgs': 4,
    'R_3-col--5-imgs': 5,
  };

  const isTwoColLayout = (layout: TGridLayouts) => {
    return [
      'E_1-img-2:1-left',
      'F_1-img-2:1-right',
      'G_1-img-16:9-left',
      'H_1-img-16:9-right',
    ].includes(layout);
  };

  const getImageSizes = (
    gridLayout: TGridLayouts,
    order: number
  ): TImageViewportWidths => {
    if (gridLayout === 'R_3-col--5-imgs') {
      return {
        mobile: order !== 2 ? '50vw' : '100vw',
        tablet: order === 1 || order === 2 ? '50vw' : '100vw',
        desktop: order !== 2 ? '25vw' : '50vw',
      };
    }

    if (
      ['2-col--2-imgs-square', '2-col--2-imgs-16:9', '2-col--2-imgs'].includes(
        gridLayout
      )
    ) {
      return {
        mobile: '100vw',
        tablet: '100vw',
        desktop: '50vw',
      };
    }

    if (['2-col--4-imgs'].includes(gridLayout)) {
      return {
        mobile: order === 2 || order === 3 ? '50vw' : '100vw',
        tablet: order === 2 || order === 3 ? '50vw' : '100vw',
        desktop: order === 2 || order === 3 ? '25vw' : '50vw',
      };
    }

    if (['2-col--4-imgs-reverse'].includes(gridLayout)) {
      return {
        mobile: order === 2 || order === 3 ? '50vw' : '100vw',
        tablet: order === 1 || order === 2 ? '25vw' : '50vw',
        desktop: order === 1 || order === 2 ? '25vw' : '50vw',
      };
    }

    if (['2-col--3-imgs'].includes(gridLayout)) {
      return {
        mobile: '100vw',
        tablet: '50vw',
        desktop: '50vw',
      };
    }

    if (['1-col--1-img', '1-col--1-img-16:9'].includes(gridLayout)) {
      return {
        mobile: '100vw',
        tablet: '100vw',
        desktop: '100vw',
      };
    }

    if (['3-col--3-imgs'].includes(gridLayout)) {
      return {
        mobile: '100vw',
        tablet: '33.33vw',
        desktop: '33.33vw',
      };
    }

    if (isTwoColLayout(gridLayout)) {
      return {
        mobile: '100vw',
        tablet: '70vw',
        desktop: '70vw',
      };
    }

    return {
      mobile: '100vw',
      tablet: '100vw',
      desktop: '100vw',
    };
  };

  const maxImages = imagesLimit[gridLayout] ?? 5;
  const mediaRefs = useRef<(HTMLImageElement | HTMLVideoElement | null)[]>([]);

  useScrollAnimation({
    animationType: scrollAnimation.type,
    elContainer: mediaRefs.current.filter(
      (el): el is HTMLImageElement | HTMLVideoElement => el !== null
    ),
  });

  if (!assets || assets.length === 0) return null;

  const isImageAsset = (asset: TImageAsset | TVideoAsset) => {
    return !(asset as TVideoAsset).vimeoId && (asset as TImageAsset).src;
  }

  return (
    <ComponentContainer
      width={width}
      marginBottom={marginBottom}
      isProject={isProject}
      marginBottomOnMobile={marginBottomOnMobile}
      animationContainer={animationContainer}
    >
      {isTwoColLayout(gridLayout) ? (
        <TwoColContainer
          isInverted={['E_1-img-2:1-left', 'G_1-img-16:9-left'].includes(
            gridLayout
          )}
          paddingTopMobile={false}
        >
          {['F_1-img-2:1-right', 'H_1-img-16:9-right'].includes(gridLayout) && (
            <div></div>
          )}
          <Styled.MediasSectionContainer
            gridLayout={gridLayout}
            isInTwoColLayout={true}
            
          >
            <Styled.ImageContainer 
              imageOrder={1}
              onClick={(e) => isImageAsset(assets[0])
                ? handleClickToggleGallerySliderModal(e, assets[0].src || '')
                : null
              }
              className={isImageAsset(assets[0]) ? 'medias-image-container' : ''}
            >
              {assets[0] && ((assets[0] as TVideoAsset)?.vimeoId ? (
                    <Video
                      src={assets[0].src}
                      poster={(assets[0] as TVideoAsset).poster}
                      vimeoMetadata={(assets[0] as TVideoAsset).vimeoMetadata}
                      {...((assets[0] as TVideoAsset).includesSound
                        ? {
                            includesSound: (assets[0] as TVideoAsset).includesSound,
                          }
                        : {})}
                      {...((assets[0] as TVideoAsset).includesControls
                        ? {
                            includesControls: (assets[0] as TVideoAsset)
                              .includesControls,
                          }
                        : {})}
                      {...((assets[0] as TVideoAsset).vimeoId
                        ? { vimeoId: (assets[0] as TVideoAsset).vimeoId }
                        : {})}
                      type={(assets[0].type as TVideoType) || 'video/mp4'}
                      fill={true}
                      autoplay="whenVisible"
                      ref={(el) => {
                        mediaRefs.current[0] = el;
                      }}
                      includeInSlider={isProject}
                      {...((assets[0] as TVideoAsset).aspectRatio
                        ? { aspectRatio: (assets[0] as TVideoAsset).aspectRatio }
                        : {})}
                    />
                  ) : (
                    <Image
                      includeInSlider={includeInSlider && isProject}
                      {...assets[0] as TImageAsset}
                      alt={(assets[0] as TImageAsset).alt}
                      lazy={orderInPage ? orderInPage > 5 : false}
                      sizes={getImageSizes(gridLayout, 0)}
                      className='medias-section-image'
                      ref={(el: HTMLImageElement | null) => {
                        mediaRefs.current[0] = el;
                      }}
                    />
                  )
                )
              }
            </Styled.ImageContainer>
          </Styled.MediasSectionContainer>
        </TwoColContainer>
      ) : (
        <Styled.MediasSectionContainer gridLayout={gridLayout}>
          {assets?.slice(0, maxImages).map((asset, i) => (
            <Styled.ImageContainer 
              key={`img-container-${i}`} 
              imageOrder={i}
              onClick={(e) => isImageAsset(asset)
                ? handleClickToggleGallerySliderModal(e, asset.src || '')
                : null
              }
              className={isImageAsset(asset) ? 'medias-image-container' : ''}
            >
              {((asset as TVideoAsset).vimeoId) ? (
                <Video
                  src={asset.src}
                  vimeoId={(asset as TVideoAsset).vimeoId}
                  vimeoMetadata={(asset as TVideoAsset).vimeoMetadata}
                  poster={(asset as TVideoAsset).poster}
                  {...((asset as TVideoAsset).includesSound
                    ? {
                      includesSound: (asset as TVideoAsset).includesSound,
                    }
                    : {})}
                  {...((asset as TVideoAsset).includesControls
                    ? {
                      includesControls: (asset as TVideoAsset)
                        .includesControls,
                    }
                    : {})}
                  type={(asset.type as TVideoType) || 'video/mp4'}
                  fill={true}
                  autoplay="whenVisible"
                  ref={(el) => {
                    mediaRefs.current[0] = el;
                  }}
                  title={titleVideo}
                  includeInSlider={isProject}
                  {...((asset as TVideoAsset).aspectRatio
                    ? { aspectRatio: (asset as TVideoAsset).aspectRatio }
                    : {})} />
              ) : (
                <Image
                  includeInSlider={includeInSlider && isProject}
                  className='medias-section-image'
                  {...asset as TImageAsset}
                  lazy={orderInPage ? orderInPage > 2 : false}
                  sizes={getImageSizes(gridLayout, i)}
                  ref={(el: HTMLImageElement | null) => {
                    mediaRefs.current[i] = el;
                  }}
                  alt={(asset as TImageAsset).alt}
                />
              )}
            </Styled.ImageContainer>
          ))}
        </Styled.MediasSectionContainer>
      )}
    </ComponentContainer>
  );
}
